<div class="entity-board-layout" [class.settings-open]="store.$viewSettingsOpen()">
  <w-page [title]="store.$context()?.header?.title ?? ''" [hideHeader]="true" [hideFooter]="true">

    <ng-template w-page-body-header>
      <board-header>
        @if (store.$showFocusSegmentTiles()) {
          <board-segment-tiles board-header-after-views />
        }
      </board-header>
      @if (store.$showSegmentTiles()) {
        <board-segment-tiles />
      }
    </ng-template>

    <ng-template w-page-body-content>
      @if (store.$layout(); as layout) {
      @switch (layout) {
        @case ('focus') {
          <div class="board-focus-layout">
            <board-focus>
              <ng-container board-focus-footer [ngTemplateOutlet]="$paginatorTemplate()" />
            </board-focus>
            <div class="board-focus-preview" [hidden]="store.$viewSettingsOpen()">
              @if ($previewContext(); as previewContext) {
                @if ($useDetailPane()) {
                  <detail-pane [context]="previewContext" />
                } @else {
                  <entity-outlet mode="inline" />
                }
              }
            </div>
          </div>
        }

        @default {
          <!-- The table's document preview is an aside INSIDE this branch: toggling it only adds/
               removes the aside + footer siblings — the display @switch stays on the same case, so
               board-table (and its ag-grid state) is never destroyed/recreated. -->
          <div class="board-body-content" [class.has-preview]="$showPreviewPanel()" [class.has-panel]="$sidePanelVisible()" [class.panel-floating]="!$panelPinned()" [class.no-header-paginator]="!store.$paged()" [class.split-dragging]="$splitDragging()" [style.--board-panel-width.px]="$panelWidth()" [style.--board-status-bar-height.px]="$statusBarHeight()">
            <div class="board-body-main">
              <div class="board-body-display" [class.is-custom]="$useCustomDisplay()">
                @if ($useCustomDisplay()) {
                  <!-- Entity-owned body: inject EntityBoardStore / ENTITY_BOARD_DATA; write selection for bulk + cmdk. -->
                  <div class="board-custom-display-host">
                    @if ($customDisplayType(); as type) {
                      <ng-container *ngComponentOutlet="type; injector: injector" />
                    }
                  </div>
                } @else {
                  @switch (store.$effectiveDisplay()) {
                    @case (EntityViewDisplay.Card) {
                      @defer (on immediate) {
                        <board-card />
                      }
                    }
                    @case (EntityViewDisplay.Board) {
                      @defer (on immediate) {
                        <board-card />
                      }
                    }
                    @case (EntityViewDisplay.Kanban) {
                      @defer (on immediate) {
                        <board-kanban />
                      }
                    }
                    @case (EntityViewDisplay.List) {
                      @defer (on immediate) {
                        <board-list />
                      }
                    }
                    @case (EntityViewDisplay.Gantt) {
                      @defer (on immediate) { <board-gantt /> }
                      @loading { <div class="board-display-state" role="status">Loading Gantt…</div> }
                      @error { <div class="board-display-state" role="alert">The Gantt could not be loaded. Refresh to try again.</div> }
                    }
                    @case (EntityViewDisplay.Calendar) {
                      @defer (on immediate) {
                        <board-calendar />
                      } @loading {
                        <div class="board-display-state" role="status">Loading calendar…</div>
                      } @error {
                        <div class="board-display-state" role="alert">The calendar could not be loaded. Refresh the page to try again.</div>
                      }
                    }
                    @case (EntityViewDisplay.Attachments) {
                      @defer (on immediate) {
                        <board-attachments />
                      }
                    }
                    @default {
                      @if (store.$serverGroups()) {
                        @defer (on immediate) {
                          <board-table-server-groups />
                        }
                      } @else {
                        @defer (on immediate) {
                          <board-table />
                        }
                      }
                    }
                  }
                }
              </div>
              @if ($showPreviewPanel() && store.$paged()) {
                <div class="board-table-detail-footer">
                  <ng-container [ngTemplateOutlet]="$paginatorTemplate()" />
                </div>
              }
            </div>

            <!-- One panel slot, one component. Both kinds size in px against the same manifest
                 bounds, so the only difference is width and whether pin chrome shows.

                 `hidden` rather than @if for the view-editor stand-down: the panel keeps its mounted
                 record, so closing the editor restores the aside without a re-fetch or a reflow.
                 A dialog presentation mounts the same panel through syncPanelDialog instead. -->
            @if (store.$panelKind(); as panelKind) {
              @if (store.$panelOpen() && store.$panelFrame() === 'side') {
                <board-panel [hidden]="!$asideVisible()" [kind]="panelKind" [ownsHeader]="$panelOwnsHeader()" [title]="$panelTitle()" [pinned]="$panelPinned()" [resizable]="$panelResizable()" [empty]="!store.$focusedItemId()" [canStep]="store.$canStep()" [stepPosition]="store.$stepPosition()" [expand]="$panelExpand()" [recordPageUrl]="$panelRecordPageUrl()" [busy]="$sidePanelBusy()" (pinnedChange)="onPanelPinnedChange($event)" (closed)="onSidePanelClose()" (stepRequested)="onPanelStep($event)" (expandRequested)="onPanelExpand()" (dockRequested)="onPanelDock()" (resizeStart)="onSplitDragStart($event)" (resizeMove)="onSplitDragMove($event)" (resizeEnd)="onSplitDragEnd($event)" (resizeReset)="onSplitReset()" (refreshRequested)="onPanelRefresh($event)" (rowPatchRequested)="store.patchDisplayedRow($event.rowId, $event.patch)" (recordUpserted)="onPanelRecordUpserted($event)">
                  @if ($previewContext(); as previewContext) {
                    @if ($useDetailPane()) {
                      <detail-pane [context]="previewContext" />
                    } @else {
                      <entity-outlet mode="inline" />
                    }
                  }
                </board-panel>
              }
            }

            @if (store.$showBulkActions() && store.$ganttWritable?.() !== false) {
              <div class="board-bulk-actions-area" [class.is-hidden]="$bulkActionsPaletteOpen()">
                <board-bulk-actions [items]="store.$selectedItems()" [commands]="store.$bulkCommands()" (clear)="store.clearSelectedItems()" (command)="onBulkCommand($event)" />
              </div>
            }
          </div>
        }
      }
      }
      @else if (!store.$headerLoading()) {
        <div class="board-empty-view">No views are available for your access.</div>
      }
    </ng-template>

  </w-page>

  @if ($context()?.settings?.component) {
    <ng-container #settingsHost></ng-container>
  } @else {
    <view-settings [isOpen]="store.$viewSettingsOpen()" [mode]="'push'" [config]="store.$viewSettingsConfig()" (previewChange)="onViewSettingsChange($event)" (valueChange)="onViewSettingsChange($event)" (saved)="onViewSettingsSaved()" (saveAsNew)="onViewSettingsSaveAsNew()" (reset)="store.resetView()" (deleted)="onViewDeleted()" (cancelled)="store.setViewSettingsOpen(false)" (closed)="store.setViewSettingsOpen(false)" />
  }
</div>

<!-- Named dialog outlet: create/edit child routes render here without destroying the board.
     Separate from preview slot outlets (primary) so both can coexist.

     TEMPORARILY COMMENTED OUT. This outlet is also named `dialog`, which collides with the shell's
     dialog outlet: a URL like /finance-pro/invoices(dialog:invoice/create) is matched against THIS
     board's children (`new`, `edit/:id`) rather than the shell's `:slug/:operation`, so the shell
     dialog is unreachable while both exist. Either rename the shell outlet to `modal`, or delete this
     one once shell-hosted create/edit is verified. -->
<!-- <entity-outlet [name]="dialogOutlet" mode="inline" /> -->
